/**
 * Application Routes — full application routing with auth, side navigation,
 * and all working components from the original app + application features.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Layers,
  Home,
  FolderOpen,
  LogOut,
  Shield,
  Brain,
  DollarSign,
  Sun,
  Moon,
  UserCircle,
  Inbox,
  KeyRound,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import { useAuth, clearAuth, setUser } from "../auth";
import { apiv2 } from "../apiv2";

// ── Original working components ──────────────────────────────
import LoginPage from "../components/LoginPage";
import ForcePasswordChange from "../components/ForcePasswordChange";
import ProfileSettings from "../components/ProfileSettings";
import LandingPage from "../components/LandingPage";
import ProjectDashboardV2 from "../components/ProjectDashboardV2";
import ProjectSetup from "../components/ProjectSetup";
import ProjectModeSelector from "../components/ProjectModeSelector";
import ProjectWorkView from "../components/ProjectWorkView";
import AutonomousOrchestrator from "../components/AutonomousOrchestrator";
import UserManagement from "../components/UserManagement";
import LLMSettings from "../components/LLMSettings";
import { OutputsInboxView } from "../components/OutputsInboxView";
import { MCPTokensAdmin, RolePermissionsAdmin } from "../features/admin";
import { GateDetail, ReviewsList } from "../features/reviews";
import { QuarantineDetail, QuarantineList } from "../features/quarantine";
import { RunCompare, RunDashboard } from "../features/runs";
import EpochSpendDashboard from "../components/EpochSpendDashboard";
import AgentCatalog from "../components/AgentCatalog";
import SemanticModelCreatorView from "../components/SemanticModelCreatorView";
import DataMaskingView from "../components/DataMaskingView";
import MetadataCatalogView from "../components/MetadataCatalogView";
import BacklogArchitectView from "../components/BacklogArchitectView";
import DataConverterView from "../components/DataConverterView";
import DataValidationView from "../components/DataValidationView";
import ConversationalAIView from "../components/ConversationalAIView";
// New modular Data Profiler (replaces 2,676-line prototype DataProfilingView)
import DataProfilerPage from "../features/data-profiler/DataProfilerPage";
// DQ Rules Engine (Days 1-4 of Functional Rules Framework MVP)
import RulesManagementView from "../components/RulesManagementView";
// MDM Agent screens
import MdmSourceSelectionView from "../features/mdm/screens/MdmSourceSelectionView";
import MdmRuleBuilderView from "../features/mdm/screens/MdmRuleBuilderView";
import MdmGoldenRecordExplorerView from "../features/mdm/screens/MdmGoldenRecordExplorerView";
import MdmRunStatusView from "../features/mdm/screens/MdmRunStatusView";
import MdmPairReviewView from "../features/mdm/screens/MdmPairReviewView";
import MdmAuditConsoleView from "../features/mdm/screens/MdmAuditConsoleView";
import { MdmLayout } from "../features/mdm/MdmLayout";
// Pipeline Engineer agent
import PipelineEngineerView from "../features/pipeline-engineer";

// ── Platform components ──────────────────────────────────────
import { ContextualSideNav } from "@/shared/components/platform/ContextualSideNav";
import { SkipLinks } from "@/shared/components/platform/SkipLinks";
import { KeyboardShortcutDialog } from "@/shared/components/platform/KeyboardShortcutDialog";
import ScrollToTopButton from "@/shared/components/platform/ScrollToTopButton";

// ============================================================
// Route wrappers (original app, preserved as-is)
// ============================================================

function ProjectSetupRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <ProjectSetup
      projectId={projectId}
      onBack={() => navigate("/projects")}
      onLaunchAgents={() => navigate(`/projects/${projectId}/agents`)}
    />
  );
}

function AgentCatalogRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <AgentCatalog
      projectId={projectId}
      onSelectAgent={(agentId: string) => {
        navigate(`/projects/${projectId}/agents/${agentId}`);
      }}
      onBack={() => navigate(`/projects/${projectId}/mode`)}
      onSetup={() => navigate(`/projects/${projectId}/setup`)}
    />
  );
}

function ProjectModeSelectorRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <ProjectModeSelector
      projectId={projectId}
      projectName=""
      onSelectAutonomous={() => navigate(`/projects/${projectId}/orchestrator`)}
      onSelectDirect={() => navigate(`/projects/${projectId}/agents`)}
      onBack={() => navigate(`/projects/${projectId}/work`)}
    />
  );
}

function ProjectWorkViewRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;

  const sessionTypeToAgent: Record<string, string> = {
    semantic: "semantic-intelligence",
    masking: "data-guardian",
    metadata: "metadata-curator",
    backlog: "backlog-architect",
    profiling: "data-profiler",
    conversion: "data-converter",
  };

  return (
    <ProjectWorkView
      projectId={projectId}
      onBack={() => navigate("/projects")}
      onStartNewWork={() => navigate(`/projects/${projectId}/mode`)}
      onDeleteProject={async () => {
        try {
          await apiv2.deleteProject(projectId);
          navigate("/projects");
        } catch (err) {
          alert(
            err instanceof Error ? err.message : "Failed to delete project",
          );
        }
      }}
      onResumeRun={(agentId: string) =>
        navigate(`/projects/${projectId}/agents/${agentId}`)
      }
      onResumeSession={(sessionType: string, sessionId: string) => {
        // MDM doesn't fit the generic /agents/:agentId?session= shape other
        // agents use -- its resume_key is a real run_id that only makes
        // sense under /runs/:runId/status (MdmRunStatusView then routes
        // onward to review/golden-records based on the run's own state).
        if (sessionType === "mdm") {
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${sessionId}/status`,
          );
          return;
        }
        const agentRoute = sessionTypeToAgent[sessionType];
        if (agentRoute)
          navigate(
            `/projects/${projectId}/agents/${agentRoute}?session=${sessionId}`,
          );
        else navigate(`/projects/${projectId}/agents`);
      }}
      onSetup={() => navigate(`/projects/${projectId}/setup`)}
      onResumeOrchestrator={() =>
        navigate(`/projects/${projectId}/orchestrator`)
      }
    />
  );
}

function AutonomousOrchestratorRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <AutonomousOrchestrator
      projectId={projectId}
      onLaunchAgent={(agentId: string) =>
        navigate(`/projects/${projectId}/agents/${agentId}`)
      }
      onBack={() => navigate(`/projects/${projectId}/mode`)}
    />
  );
}

function DataProfilingRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeSessionId = searchParams.get("session") || undefined;
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    // April 2026: w-full + min-w-0 so DataProfilerPage's flex chain can
    // grow to fill the available width — flex children with default
    // min-width:auto were holding it at ~656px on some layouts.
    <div className="overflow-y-auto h-full w-full min-w-0 custom-scrollbar relative z-10">
      <DataProfilerPage
        onBack={() => navigate(-1)}
        projectId={projectId}
        resumeSessionId={resumeSessionId}
      />
    </div>
  );
}

function DataModelCreatorRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeSessionId = searchParams.get("session") || undefined;
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <SemanticModelCreatorView
        onBack={() => navigate(-1)}
        projectId={projectId}
        resumeSessionId={resumeSessionId}
      />
    </div>
  );
}

function BacklogArchitectRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <BacklogArchitectView onBack={() => navigate(-1)} projectId={projectId} />
    </div>
  );
}

function DataMaskingRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeSessionId = searchParams.get("session") || undefined;
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <DataMaskingView
        onBack={() => navigate(-1)}
        projectId={projectId}
        resumeSessionId={resumeSessionId}
      />
    </div>
  );
}

function ConversationalAIRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <ConversationalAIView onBack={() => navigate(-1)} projectId={projectId} />
    </div>
  );
}

function MetadataCatalogRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeSessionId = searchParams.get("session") || undefined;
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <MetadataCatalogView
        onBack={() => navigate(-1)}
        projectId={projectId}
        resumeSessionId={resumeSessionId}
      />
      <ScrollToTopButton />
    </div>
  );
}

function DataConverterRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <DataConverterView onBack={() => navigate(-1)} projectId={projectId} />
    </div>
  );
}

function DataValidationRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <DataValidationView onBack={() => navigate(-1)} projectId={projectId} />
    </div>
  );
}

function PipelineEngineerRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <div className="overflow-y-auto h-full w-full min-w-0 custom-scrollbar relative z-10">
      <PipelineEngineerView projectId={projectId} onBack={() => navigate(-1)} />
    </div>
  );
}

// ============================================================
// DQ Rules Engine routes
// ============================================================

function RulesManagementRoute() {
  // April 2026: DQ Rules Engine merged into the unified
  // "Profiler & Quality Rules Engine" agent. This route now redirects
  // existing bookmarks to the merged agent's Rules tab. The
  // RulesManagementView component itself remains in place so the
  // Profiler page can still embed it as a tab.
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <Navigate
      to={`/projects/${projectId}/agents/data-profiler?tab=rules`}
      replace
    />
  );
}

// Standalone profiler (no project context — accessed from side nav)
function ProfilerStandalone() {
  const navigate = useNavigate();
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <DataProfilerPage onBack={() => navigate("/")} />
    </div>
  );
}

function ProfilerSettingsPage() {
  const navigate = useNavigate();
  return <ProfileSettings onBack={() => navigate("/profiler")} />;
}

function ProfilerSessionRoute() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <DataProfilerPage
        onBack={() => navigate("/profiler")}
        resumeSessionId={sessionId}
      />
    </div>
  );
}

// ============================================================
// MDM Agent routes
// ============================================================

function MdmNewRunRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  if (!projectId) return <Navigate to="/projects" replace />;
  return (
    <MdmLayout currentScreen="source-selection">
      <MdmSourceSelectionView
        projectId={projectId}
        onComplete={(runId, entityType) =>
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${runId}/config`,
          )
        }
        onBack={() => navigate(`/projects/${projectId}/agents`)}
        onViewGoldenRecords={(runId) =>
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${runId}/golden-records`,
          )
        }
      />
    </MdmLayout>
  );
}

function MdmRuleBuilderRoute() {
  const { projectId, runId } = useParams<{
    projectId: string;
    runId: string;
  }>();
  const navigate = useNavigate();
  if (!projectId || !runId) return <Navigate to="/projects" replace />;
  return (
    <MdmLayout currentScreen="rule-builder">
      <MdmRuleBuilderView
        runId={runId}
        projectId={projectId}
        // The match config is built from the source's DISCOVERED fields
        // (fetched inside the view). This is only a fallback for the brief
        // window before discovery resolves — intentionally empty so a wrong
        // hardcoded schema never drives grouping/matching. See
        // MdmRuleBuilderView's getFields effect + pickDefaultBlockingKeys.
        canonicalFields={[]}
        onTestRun={() =>
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${runId}/status`,
          )
        }
      />
    </MdmLayout>
  );
}

function MdmGoldenRecordsRoute() {
  const { projectId, runId } = useParams<{
    projectId: string;
    runId: string;
  }>();
  const navigate = useNavigate();
  if (!projectId || !runId) return <Navigate to="/projects" replace />;
  return (
    <MdmLayout currentScreen="golden-records">
      <MdmGoldenRecordExplorerView
        runId={runId}
        projectId={projectId}
        onNewRun={() => navigate(`/projects/${projectId}/agents/mdm-agent/new`)}
        onNavigateToAudit={() =>
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${runId}/audit`,
          )
        }
      />
    </MdmLayout>
  );
}

function MdmRunStatusRoute() {
  const { projectId, runId } = useParams<{
    projectId: string;
    runId: string;
  }>();
  const navigate = useNavigate();
  if (!projectId || !runId) return <Navigate to="/projects" replace />;
  return (
    <MdmLayout currentScreen="run-status">
      <MdmRunStatusView
        runId={runId}
        onNavigateToGoldenRecords={() =>
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${runId}/golden-records`,
          )
        }
        onNavigateToReview={() =>
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${runId}/review`,
          )
        }
      />
    </MdmLayout>
  );
}

function MdmPairReviewRoute() {
  const { projectId, runId } = useParams<{
    projectId: string;
    runId: string;
  }>();
  const navigate = useNavigate();
  if (!projectId || !runId) return <Navigate to="/projects" replace />;
  return (
    <MdmLayout currentScreen="run-status">
      <MdmPairReviewView
        runId={runId}
        onBack={() =>
          navigate(
            `/projects/${projectId}/agents/mdm-agent/runs/${runId}/status`,
          )
        }
      />
    </MdmLayout>
  );
}

function MdmAuditRoute() {
  const { projectId, runId } = useParams<{
    projectId: string;
    runId: string;
  }>();
  if (!projectId || !runId) return <Navigate to="/projects" replace />;
  return (
    <MdmLayout currentScreen="golden-records">
      <MdmAuditConsoleView runId={runId} projectId={projectId} />
    </MdmLayout>
  );
}

// ============================================================
// Main App with Auth + Application Chrome
// ============================================================

export function AppRoutes() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  // A tab opened via window.open (e.g. "Open Data Profiler" deep-linking to
  // another agent) shares the HttpOnly auth cookie with the tab it came
  // from, but sessionStorage's cached user profile is per-tab -- so this
  // starts with no cached user even though the cookie is still valid.
  // Block on validating the cookie via /auth/me before ever deciding to
  // show LoginPage, instead of only re-validating when a cached profile
  // already exists (which silently forced a real login in that new tab).
  //
  // Only do this when there's actually a session to validate: either a
  // cached profile in this tab, or the (JS-readable, non-HttpOnly)
  // ztd_csrf_token cookie, which the backend only ever sets alongside the
  // HttpOnly access-token cookie on login (see auth/v2_router.py's
  // _set_auth_cookies). A genuinely fresh browser/tab that has never
  // logged in has neither, so skip the round trip entirely and render
  // LoginPage immediately -- calling /auth/me unconditionally here also
  // hits JWTAuthMiddleware's dev-mode (ZTD_AUTH_COOKIE_SECURE=False)
  // no-cookie fallback, which synthesizes a platform_admin user and
  // silently auto-authenticates every cold page load in local/E2E
  // environments, never showing LoginPage at all.
  const hasSessionCookie = () =>
    /(?:^|;\s*)ztd_csrf_token=/.test(document.cookie);

  const [checkingSession, setCheckingSession] = useState(
    !auth.isLoggedIn && hasSessionCookie(),
  );

  useEffect(() => {
    if (!auth.isLoggedIn && !hasSessionCookie()) return;

    apiv2
      .me()
      .then((user) =>
        setUser({
          id: user.id,
          email: user.email,
          full_name: user.full_name,
          role: user.role,
          permissions: user.permissions ?? [],
          is_active: user.is_active,
          must_change_password: user.must_change_password,
        }),
      )
      .catch(() => clearAuth())
      .finally(() => setCheckingSession(false));
  }, []);

  const handleLogout = useCallback(async () => {
    await apiv2.logout();
    clearAuth();
  }, []);

  // ── Still validating the auth cookie (see checkingSession above) ──
  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-500">
        <Loader2 size={24} className="animate-spin text-white/40" />
      </div>
    );
  }

  // ── Not logged in → LoginPage ──────────────────────
  // 2026-09-16, reported by user: a session expiring (or a page refresh
  // hitting the "no cookie yet" cold-start race above) while deep in an
  // agent flow (e.g. /projects/{id}/agents/data-converter) bounced the
  // user to the app's root landing page after logging back in, losing all
  // context — this always navigated to "/" regardless of where the user
  // actually was. Return to the exact path they were on instead.
  if (!auth.isLoggedIn) {
    const returnTo = location.pathname + location.search;
    return (
      <LoginPage onLoginSuccess={() => navigate(returnTo, { replace: true })} />
    );
  }

  // ── Force password change ──────────────────────────
  if (auth.user?.must_change_password) {
    return <ForcePasswordChange onComplete={() => navigate("/")} />;
  }

  const isHome = location.pathname === "/";

  return (
    <>
      <SkipLinks />
      <div
        className={`min-h-screen flex ${isDark ? "bg-surface-500 text-white" : "bg-[#F5F7FA] text-[#1a1a2e]"}`}
      >
        {/* ── Side Navigation (platform) ─────────────── */}
        <ContextualSideNav
          currentPath={location.pathname}
          onNavigate={(path) => navigate(path)}
        />

        {/* ── Main content area ──────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* ── Top Bar ────────────────────────────────── */}
          <header
            className="flex items-center justify-between px-6 py-3 border-b border-white/5 backdrop-blur-xl relative z-50"
            style={{
              background: isDark
                ? "rgba(14,14,24,0.85)"
                : "rgba(255,255,255,0.88)",
            }}
          >
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-brand-500 hover:text-brand-400 transition"
            >
              <Layers size={22} />
              <span className="text-sm font-bold tracking-wide">
                ZTD<span className="text-white/30 font-normal">.si</span>
              </span>
            </button>

            <div className="flex items-center gap-2">
              {!isHome && (
                <>
                  <button
                    onClick={() => navigate("/projects")}
                    className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                  >
                    <FolderOpen size={14} /> Projects
                  </button>
                  <button
                    data-testid="nav-reviews"
                    onClick={() => navigate("/reviews")}
                    className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                  >
                    <Inbox size={14} /> Reviews
                  </button>
                  {auth.isAdmin && (
                    <button
                      data-testid="nav-quarantine"
                      onClick={() => navigate("/quarantine")}
                      className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                    >
                      <ShieldAlert size={14} /> Quarantine
                    </button>
                  )}
                  {auth.isAdmin && (
                    <button
                      onClick={() => navigate("/admin/users")}
                      className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                    >
                      <Shield size={14} /> Users
                    </button>
                  )}
                  {auth.isAdmin && (
                    <button
                      onClick={() => navigate("/admin/roles")}
                      className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                    >
                      <KeyRound size={14} /> Roles
                    </button>
                  )}
                  {auth.isAdmin && (
                    <button
                      onClick={() => navigate("/admin/llm")}
                      className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                    >
                      <Brain size={14} /> LLM
                    </button>
                  )}
                  {auth.isAdmin && (
                    <button
                      data-testid="nav-epoch-spend"
                      onClick={() => navigate("/admin/epoch-spend")}
                      className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                    >
                      <DollarSign size={14} /> Spend
                    </button>
                  )}
                  {auth.isAdmin && (
                    <button
                      data-testid="nav-mcp-tokens"
                      onClick={() => navigate("/admin/mcp-tokens")}
                      className="flex items-center gap-1.5 text-xs text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                    >
                      <Layers size={14} /> MCP
                    </button>
                  )}
                  <button
                    onClick={() => navigate("/")}
                    className="flex items-center gap-1.5 text-xs text-white/30 hover:text-white/60 transition px-2 py-1 rounded-lg hover:bg-white/5"
                  >
                    <Home size={14} />
                  </button>
                </>
              )}

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="flex items-center gap-1.5 text-xs transition px-2 py-1.5 rounded-lg hover:bg-white/5"
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                style={{
                  color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.5)",
                }}
              >
                {isDark ? <Sun size={15} /> : <Moon size={15} />}
                <span className="hidden sm:inline">
                  {isDark ? "Light" : "Dark"}
                </span>
              </button>

              {/* User menu */}
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-white/8">
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center gap-1.5 text-[11px] text-white/30 hover:text-brand-500 transition px-2 py-1 rounded-lg hover:bg-white/5"
                  title="Profile settings"
                >
                  <UserCircle size={14} />
                  <span className="hidden sm:inline">
                    {auth.user?.full_name}
                  </span>
                </button>
                <span className="text-[11px] text-brand-400/60 bg-brand-500/10 px-1.5 py-0.5 rounded hidden sm:inline capitalize">
                  {auth.user?.role === "conference_attendee" ||
                  auth.user?.role === "demo_converter_guardian_profiler"
                    ? "Demo User"
                    : auth.user?.role.replace("_", " ")}
                </span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1 text-xs text-white/20 hover:text-red-400 transition px-2 py-1 rounded-lg hover:bg-red-500/10"
                  title="Sign out"
                >
                  <LogOut size={14} />
                </button>
              </div>
            </div>
          </header>

          {/* ── Content ──────────────────────────────── */}
          <main
            id="main-content"
            className="flex-1 overflow-hidden relative"
            tabIndex={-1}
          >
            <div className="absolute inset-0 dot-grid pointer-events-none" />
            <div className="glow-orb glow-orb-1 pointer-events-none" />
            <div className="glow-orb glow-orb-2 pointer-events-none" />

            <Routes>
              {/* ── Home ──────────────────────────────── */}
              <Route
                path="/"
                element={
                  <LandingPage onGetStarted={() => navigate("/projects")} />
                }
              />

              {/* ── Projects ─────────────────────────── */}
              <Route
                path="/projects"
                element={
                  <ProjectDashboardV2
                    onBack={() => navigate("/")}
                    onSelectProject={(id: string) =>
                      navigate(`/projects/${id}/work`)
                    }
                    onSetupProject={(id: string) =>
                      navigate(`/projects/${id}/setup`)
                    }
                  />
                }
              />

              <Route
                path="/projects/:projectId/work"
                element={<ProjectWorkViewRoute />}
              />
              <Route
                path="/projects/:projectId/setup"
                element={<ProjectSetupRoute />}
              />
              <Route
                path="/projects/:projectId/mode"
                element={<ProjectModeSelectorRoute />}
              />
              <Route
                path="/projects/:projectId/orchestrator"
                element={<AutonomousOrchestratorRoute />}
              />
              <Route
                path="/projects/:projectId/agents"
                element={<AgentCatalogRoute />}
              />

              {/* ── Agent views (project-scoped) ─────── */}
              <Route
                path="/projects/:projectId/agents/data-profiler"
                element={<DataProfilingRoute />}
              />
              <Route
                path="/projects/:projectId/agents/semantic-intelligence"
                element={<DataModelCreatorRoute />}
              />
              <Route
                path="/projects/:projectId/agents/data-guardian"
                element={<DataMaskingRoute />}
              />
              <Route
                path="/projects/:projectId/agents/metadata-curator"
                element={<MetadataCatalogRoute />}
              />
              <Route
                path="/projects/:projectId/agents/backlog-architect"
                element={<BacklogArchitectRoute />}
              />
              <Route
                path="/projects/:projectId/agents/data-converter"
                element={<DataConverterRoute />}
              />
              <Route
                path="/projects/:projectId/agents/validation"
                element={<DataValidationRoute />}
              />
              <Route
                path="/projects/:projectId/agents/pipeline-engineer"
                element={<PipelineEngineerRoute />}
              />
              <Route
                path="/projects/:projectId/agents/conversational-ai"
                element={<ConversationalAIRoute />}
              />

              {/* ── DQ Rules Engine ─────────────────── */}
              <Route
                path="/projects/:projectId/agents/rules-engine"
                element={<RulesManagementRoute />}
              />
              {/*
                Standalone agent routes are redirected to /projects.
                All agent execution is scoped to a project — these deep
                links remain to avoid 404s for old bookmarks but funnel
                users into the project-selection flow.
              */}
              <Route
                path="/rules"
                element={<Navigate to="/projects" replace />}
              />
              <Route
                path="/profiler"
                element={<Navigate to="/projects" replace />}
              />
              <Route
                path="/profiler/new"
                element={<Navigate to="/projects" replace />}
              />
              <Route
                path="/profiler/history"
                element={<Navigate to="/projects" replace />}
              />
              <Route
                path="/profiler/session/:sessionId"
                element={<ProfilerSessionRoute />}
              />
              <Route
                path="/profiler/settings"
                element={<Navigate to="/projects" replace />}
              />

              {/* ── Admin ────────────────────────────── */}
              {auth.isAdmin && (
                <Route
                  path="/admin/users"
                  element={<UserManagement onBack={() => navigate("/")} />}
                />
              )}
              {auth.isAdmin && (
                <Route
                  path="/admin/roles"
                  element={
                    <RolePermissionsAdmin onBack={() => navigate("/")} />
                  }
                />
              )}
              {auth.isAdmin && (
                <Route
                  path="/admin/llm"
                  element={<LLMSettings onBack={() => navigate("/")} />}
                />
              )}
              {auth.isAdmin && (
                <Route
                  path="/admin/epoch-spend"
                  element={<EpochSpendDashboard onBack={() => navigate("/")} />}
                />
              )}
              {auth.isAdmin && (
                <Route
                  path="/admin/mcp-tokens"
                  element={<MCPTokensAdmin onBack={() => navigate("/")} />}
                />
              )}

              {/* ── Outputs / Review inbox ──────────────── */}
              <Route path="/outputs" element={<OutputsInboxView />} />

              {/* ── Reviews (HITL) ──────────────────────── */}
              <Route
                path="/reviews"
                element={<ReviewsList onBack={() => navigate("/")} />}
              />
              <Route
                path="/reviews/:gateId"
                element={<GateDetail onBack={() => navigate("/reviews")} />}
              />

              {/* ── Quarantine (malware-scan review queue) ─ */}
              <Route
                path="/quarantine"
                element={<QuarantineList onBack={() => navigate("/")} />}
              />
              <Route
                path="/quarantine/:itemId"
                element={
                  <QuarantineDetail onBack={() => navigate("/quarantine")} />
                }
              />

              {/* ── Run comparison (P11b) ──────────────── */}
              <Route
                path="/runs/compare"
                element={<RunCompare onBack={() => navigate("/")} />}
              />

              {/* ── Run report dashboard (P10h) ─────────── */}
              <Route
                path="/runs/:id/dashboard"
                element={<RunDashboard onBack={() => navigate("/")} />}
              />

              {/* ── Profile ──────────────────────────── */}
              <Route
                path="/profile"
                element={<ProfileSettings onBack={() => navigate("/")} />}
              />

              {/* ── MDM Agent ─────────────────────────── */}
              <Route
                path="/projects/:projectId/agents/mdm-agent"
                element={<MdmNewRunRoute />}
              />
              <Route
                path="/projects/:projectId/agents/mdm-agent/runs/:runId/config"
                element={<MdmRuleBuilderRoute />}
              />
              <Route
                path="/projects/:projectId/agents/mdm-agent/runs/:runId/golden-records"
                element={<MdmGoldenRecordsRoute />}
              />
              <Route
                path="/projects/:projectId/agents/mdm-agent/runs/:runId/status"
                element={<MdmRunStatusRoute />}
              />
              <Route
                path="/projects/:projectId/agents/mdm-agent/runs/:runId/review"
                element={<MdmPairReviewRoute />}
              />
              <Route
                path="/projects/:projectId/agents/mdm-agent/runs/:runId/audit"
                element={<MdmAuditRoute />}
              />

              {/* ── Catch-all ────────────────────────── */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          {/* ── Footer ───────────────────────────────── */}
          {/* Hidden on the MDM agent screens — those are dense config/review
              surfaces where the application byline reads as clutter (no other
              agent surfaces it there either). */}
          {!location.pathname.includes("/agents/mdm-agent") && (
            <footer
              className={`text-center text-[11px] py-2 border-t border-white/5 relative z-10 ${
                isDark ? "text-white/40" : "text-black/40"
              }`}
            >
              Zero Touch Data &mdash; Agentic AI Application for Data Management
            </footer>
          )}
        </div>
      </div>
      <KeyboardShortcutDialog />
    </>
  );
}
